import { Box, Button, Flex, Heading, Text } from "@chakra-ui/react";
import { useNavigate, useSearchParams } from "react-router-dom";

const PATHPICK_HOME = "/pathpick/search-products";

/**
 * 카페24 연동 결과 안내.
 *
 * 백엔드 OAuth 콜백(`/pathpick/cafe24/oauth/callback`)이 처리 결과를 `?status=...` 로 달아
 * 이 페이지로 돌려보낸다. 설치 경로(바이어 링크 전)는 여기가 아니라 온보딩으로 간다.
 * 로그인 없이도 열릴 수 있어야 한다 — 카페24에서 바로 튕겨 오는 실패 케이스가 있다.
 */

type Copy = { title: string; description: (mallId: string | null) => string };

const SUCCESS_STATUS = "connected";

const COPY_BY_STATUS: Record<string, Copy> = {
  connected: {
    title: "연동 완료",
    description: (mallId) =>
      mallId
        ? `‘${mallId}’ 카페24 쇼핑몰이 패스레이드에 연동되었어요.`
        : "카페24 쇼핑몰이 패스레이드에 연동되었어요.",
  },
  denied: {
    title: "연동이 취소되었어요",
    description: () =>
      "권한 동의가 완료되지 않았어요. 카페24 관리자 > 앱 > 내 앱에서 패스레이드를 다시 실행해 주세요.",
  },
  code_expired: {
    title: "인증 시간이 지났어요",
    description: () =>
      "동의 화면에 머무는 사이 인증 코드가 만료되었어요. 처음부터 다시 시도해 주세요.",
  },
  invalid_state: {
    title: "연동 정보를 확인할 수 없어요",
    description: () =>
      "인증 요청이 만료되었거나 올바르지 않아요. 카페24 관리자에서 패스레이드를 다시 실행해 주세요.",
  },
  conflict: {
    title: "이미 연결된 쇼핑몰이에요",
    description: () =>
      "이 쇼핑몰은 다른 계정에 연결되어 있거나, 이 계정에 다른 쇼핑몰이 이미 연결되어 있어요. 기존 연결을 해제한 뒤 다시 시도해 주세요.",
  },
  invalid_signature: {
    title: "올바른 접근이 아니에요",
    description: () =>
      "카페24를 통한 접근이 아니에요. 카페24 관리자 > 앱 > 내 앱에서 패스레이드를 실행해 주세요.",
  },
  direct_access: {
    title: "올바른 접근이 아니에요",
    description: () =>
      "주소를 직접 여신 것 같아요. 카페24 관리자 > 앱 > 내 앱에서 패스레이드를 실행해 주세요.",
  },
};

const FALLBACK: Copy = {
  title: "연동을 완료하지 못했어요",
  description: () =>
    "잠시 후 다시 시도해 주세요. 문제가 계속되면 고객센터로 알려 주세요.",
};

export default function Cafe24ResultPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const status = searchParams.get("status") ?? "";
  const mallId = searchParams.get("mall_id");
  // 카페24가 돌려준 원인 코드(invalid_scope 등) — 설정 문제를 사용자 거부로 오해하지 않게 함께 보여준다.
  const error = searchParams.get("error");
  const copy = COPY_BY_STATUS[status] ?? FALLBACK;
  const isSuccess = status === SUCCESS_STATUS;

  return (
    <Box padding="24px" height="100%" overflow="auto" bg="#FCFCFE">
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minH="500px"
        gap="16px"
        textAlign="center"
      >
        <Heading fontSize="28px" fontWeight="700" color="#303136">
          {copy.title}
        </Heading>
        <Text fontSize="16px" color="#8F8F8C" maxW="420px">
          {copy.description(mallId)}
        </Text>
        {error && (
          <Text fontSize="13px" color="#B0B0AE">
            오류 코드: {error}
          </Text>
        )}
        <Button
          mt="8px"
          bg={isSuccess ? "#1272EF" : undefined}
          color={isSuccess ? "white" : "#303136"}
          variant={isSuccess ? "solid" : "outline"}
          borderColor={isSuccess ? undefined : "#D9D9D9"}
          _hover={isSuccess ? { bg: "#0F5FCF" } : undefined}
          onClick={() => navigate(PATHPICK_HOME)}
        >
          패스픽으로 이동
        </Button>
      </Flex>
    </Box>
  );
}
